import axios from "axios";
import Cookies from "js-cookie";
import {
  displayReadableText,
  hostnameFromUrl,
  isGarbageOgTitle,
  titleFromUrl,
} from "../utils/displayReadableUrl";

export type LinkMetadata = {
  title: string;
  description: string;
  /** Real OG/Twitter image (not favicon). Empty → compact card. */
  image: string;
  /** Site icon for compact card left slot. */
  favicon: string;
  publisher: string;
};

const CACHE_PREFIX = "metadata_v6_";
const FETCH_TIMEOUT_MS = 7000;
const inflight = new Map<string, Promise<LinkMetadata>>();

function isFaviconLike(src: string): boolean {
  if (!src) return true;
  const n = src.toLowerCase().split("?")[0];
  return (
    n.endsWith(".ico") ||
    n.includes("favicon") ||
    n.endsWith("/favicon.png") ||
    n.endsWith("/favicon.jpg") ||
    n.includes("/apple-touch-icon") ||
    n.includes("passport.yandex") ||
    n.includes("sso.") ||
    n.endsWith(".svg") // often logo, not 1200×630 hero
  );
}

/** Instant favicon guess — works before network OG finishes. */
export function faviconGuessUrl(pageUrl: string): string {
  const host = hostnameFromUrl(pageUrl);
  if (!host) return "";
  // s2/favicons usually returns 200 + default icon (avoids noisy faviconV2 404s)
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`;
}

/** Prefer stable favicon URL; drop flaky gstatic faviconV2 that 404 in console. */
export function safeFaviconUrl(pageUrl: string, candidate?: string | null): string {
  const guess = faviconGuessUrl(pageUrl);
  const c = (candidate || "").trim();
  if (!c) return guess;
  if (/faviconv2|t\d\.gstatic\.com\/favicon/i.test(c)) return guess;
  return c;
}

function normalizeMetadata(
  raw: {
    title?: string;
    description?: string;
    image?: string;
    favicon?: string;
    publisher?: string;
  },
  pageUrl: string,
): LinkMetadata {
  let title = displayReadableText(raw.title || "");
  let description = displayReadableText(raw.description || "");
  let publisher = displayReadableText(raw.publisher || "");

  if (isGarbageOgTitle(raw.title || "") || isGarbageOgTitle(title)) {
    title = titleFromUrl(pageUrl);
  }
  if (!title) title = titleFromUrl(pageUrl);

  const host = hostnameFromUrl(pageUrl);
  if (
    publisher &&
    /yandex\.ru|passport/i.test(publisher) &&
    host &&
    !/yandex\.ru$/i.test(host)
  ) {
    publisher = host;
  }
  if (!publisher) publisher = host;

  if (
    description &&
    (description === title ||
      isGarbageOgTitle(raw.description || "") ||
      isGarbageOgTitle(description))
  ) {
    description = "";
  }

  let image = (raw.image || "").trim();
  let favicon = (raw.favicon || "").trim();

  // Don't treat favicon as OG hero image
  if (image && isFaviconLike(image)) {
    if (!favicon) favicon = image;
    image = "";
  }

  favicon = safeFaviconUrl(pageUrl, favicon);

  return { title, description, image, favicon, publisher };
}

function readCache(url: string): LinkMetadata | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + encodeURIComponent(url));
    if (!raw) return null;
    return normalizeMetadata(JSON.parse(raw), url);
  } catch {
    return null;
  }
}

function writeCache(url: string, meta: LinkMetadata) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + encodeURIComponent(url),
      JSON.stringify(meta),
    );
  } catch {
    /* quota */
  }
}

function hasUsefulTitle(meta: LinkMetadata, pageUrl: string): boolean {
  const host = hostnameFromUrl(pageUrl).toLowerCase();
  const t = (meta.title || "").trim().toLowerCase();
  if (!t || isGarbageOgTitle(meta.title)) return false;
  // host-only fallback is not a real page title
  if (t === host || t === `www.${host}`) return false;
  return true;
}

async function fetchFromMicrolink(url: string): Promise<LinkMetadata | null> {
  try {
    const response = await axios.get(`https://api.microlink.io`, {
      params: {
        url,
        video: false,
        audio: false,
        iframe: false,
      },
      timeout: FETCH_TIMEOUT_MS,
    });

    if (response.data?.status === "fail") return null;

    const data = response.data?.data || {};
    const finalUrl = String(data.url || "");
    const authWall = /passport\.|sso\.|\/login|\/signin/i.test(finalUrl);

    if (authWall) return null;

    const ogImage = String(data.image?.url || "").trim();
    const logo = String(data.logo?.url || "").trim();

    return normalizeMetadata(
      {
        title: data.title || "",
        description: data.description || "",
        image: ogImage && !isFaviconLike(ogImage) ? ogImage : "",
        favicon: logo || (isFaviconLike(ogImage) ? ogImage : "") || "",
        publisher: data.publisher || "",
      },
      url,
    );
  } catch {
    return null;
  }
}

/** Our backend: fetch HTML + parse <title> / og:* (works when microlink times out). */
async function fetchFromOurProxy(url: string): Promise<LinkMetadata | null> {
  try {
    const token = Cookies.get("whoxa_web_token");
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL || ""}/api/config/link-preview`,
      {
        params: { url },
        timeout: FETCH_TIMEOUT_MS + 2000,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        // silent — don't trip global error modal
        validateStatus: (s) => s >= 200 && s < 300,
      },
    );
    const data = response.data?.data;
    if (!data) return null;
    return normalizeMetadata(
      {
        title: data.title || "",
        description: data.description || "",
        image: data.image || "",
        favicon: data.favicon || "",
        publisher: data.publisher || "",
      },
      url,
    );
  } catch {
    return null;
  }
}

/**
 * Instant local fallback (title/host/favicon guess) — never blocks UI.
 */
export function metadataFallback(url: string): LinkMetadata {
  return normalizeMetadata({}, url);
}

/**
 * Cached / network OG parse. Dedupes concurrent requests for the same URL.
 * Order: our server crawler (same as send persist) → microlink → host fallback.
 */
export async function fetchMetadata(url: string): Promise<LinkMetadata> {
  const cached = readCache(url);
  if (cached && hasUsefulTitle(cached, url)) return cached;

  const existing = inflight.get(url);
  if (existing) return existing;

  const job = (async () => {
    try {
      // Prefer backend Webpage crawler (MTS-hosted image/favicon, no client SSO)
      let meta = await fetchFromOurProxy(url);
      if (!meta || !hasUsefulTitle(meta, url)) {
        const fromMicrolink = await fetchFromMicrolink(url);
        if (fromMicrolink && hasUsefulTitle(fromMicrolink, url)) {
          meta = meta
            ? {
                ...fromMicrolink,
                image: meta.image || fromMicrolink.image,
                favicon: meta.favicon || fromMicrolink.favicon,
              }
            : fromMicrolink;
        } else if (!meta) {
          meta = fromMicrolink || metadataFallback(url);
        }
      }
      writeCache(url, meta!);
      return meta!;
    } catch {
      return metadataFallback(url);
    } finally {
      inflight.delete(url);
    }
  })();

  inflight.set(url, job);
  return job;
}

/** Warm cache without awaiting (optional). */
export function prefetchMetadata(url: string) {
  void fetchMetadata(url);
}
